import { classNames } from "@certa/catalyst/utils/common";
import type {
  ColumnResizeMode,
  Header,
  ColumnResizeDirection,
  TableState
} from "../../Table.types";

import styles from "./InternalTableHeaderResizer.module.css";

export type InternalTableHeaderResizerProps<TableData> = {
  header: Header<TableData, unknown>;
  columnResizeMode: ColumnResizeMode;
  columnResizeDirection: ColumnResizeDirection;
  columnSizingInfo: TableState["columnSizingInfo"];
};

export const InternalTableHeaderResizer = <
  TableData extends Record<string, any>
>(
  props: InternalTableHeaderResizerProps<TableData>
) => {
  const { header, columnResizeDirection, columnResizeMode, columnSizingInfo } =
    props;

  // Resizing becomes very slow if there are more than 200 rows.
  // As of now, we do not have use cases where we have more than 200 rows.
  // So, we are not prioritizing the performance of columnResizing.
  // TODO: https://thevetted.atlassian.net/browse/PLAT-21609
  const canResize = header.column.getCanResize();
  const isResizing = header.column.getIsResizing();

  // This transform is used to move the resizer to the correct position while columnResizing
  const columnResizeStyleTransform =
    columnResizeMode === "onEnd" && isResizing
      ? `translateX(${
          (columnResizeDirection === "rtl" ? -1 : 1) *
          (columnSizingInfo.deltaOffset ?? 0)
        }px)`
      : "";

  if (!canResize) {
    return null;
  }

  return (
    <div
      data-testid={`column-resizer-${header.column.id}`}
      onDoubleClick={header.column.resetSize}
      onMouseDown={header.getResizeHandler()}
      onMouseUp={evt => {
        evt.preventDefault();
      }}
      onTouchStart={header.getResizeHandler()}
      className={classNames({
        [styles.columnResizer]: true,
        [styles.isColumnResizing]: header.column.getIsResizing(),
        [styles.ltr]: columnResizeDirection === "ltr",
        [styles.rtl]: columnResizeDirection === "rtl"
      })}
      style={{
        transform: columnResizeStyleTransform
      }}
    />
  );
};
